import { Resume } from 'packages/types'
import { useI18n } from 'builder/hooks/useI18n'
import { useGetResume } from 'builder/hooks/useGetResume'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { formatDocumentPreviewUrl } from 'builder/utils/formatDocumentPreviewUrl'

import {
  DESKTOP_PREVIEW_WIDTH,
  PHONE_PREVIEW_WIDTH,
} from 'builder/components/Panel/Document/constants'
import { ResumeFieldSuggest } from 'builder/modules/resumeEditor'
import { getScoreSuggestions } from 'builder/modules/resumeEditor/resumeScoreOracle'
import { ResumeCraftingInfo, ResumeCraftingStatus } from '../types'
import { TRANSLATION } from '../../../constants'
import { Service } from '../../../../types'

type ResumeCraftingHookConfig = {
  resume?: Resume
  isLoading: boolean
  text: ResumeCraftingInfo
  status: ResumeCraftingStatus
  suggestion: ResumeFieldSuggest | undefined
  resumePreview?: string
}

type ResumeCraftingHookConfigProps = {
  resumeId?: number
  service: Service
}

function classifyScore(score: number) {
  const goodScore = score > 70
  const okScore = score > 30 && score <= 70
  const badScore = score <= 30

  return { badScore, okScore, goodScore }
}

export const useResumeCraftingConfig = ({
  resumeId,
  service,
}: ResumeCraftingHookConfigProps): ResumeCraftingHookConfig => {
  const { percentageProgress = 0 } = service.calculatedProgress
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()
  const {
    data: resume,
    isFetching,
    isLoading,
  } = useGetResume({
    id: percentageProgress >= 100 || percentageProgress === 0 ? undefined : resumeId,
    cacheTime: 0,
  })
  const { suggestions } = getScoreSuggestions(resume as Resume)

  const getStatus = () => {
    if (percentageProgress >= 100) return 'completed'
    if (!resumeId || !resume) return 'initial'
    return 'progress'
  }

  const getText = (): ResumeCraftingInfo => {
    const status = getStatus()
    const key = `resume_crafting.${status}`

    if (status === 'progress') {
      const { badScore, okScore, goodScore } = classifyScore(resume?.resumeScore || 0)

      let scoreKey = null
      if (badScore) {
        scoreKey = 'bad_score'
      } else if (okScore) {
        scoreKey = 'ok_score'
      } else if (goodScore) {
        scoreKey = 'good_score'
      }

      return {
        badge: i18n.t(`${TRANSLATION}.${key}.${scoreKey}.badge`),
        header: i18n.t(`${TRANSLATION}.${key}.${scoreKey}.header`),
        subHeader: i18n.t(`${TRANSLATION}.${key}.${scoreKey}.subHeader`),
        actionText: i18n.t(`${TRANSLATION}.${key}.${scoreKey}.actionText`),
        scoreText: i18n.t(`${TRANSLATION}.${key}.${scoreKey}.scoreText`),
      }
    }

    return {
      badge: i18n.t(`${TRANSLATION}.${key}.badge`),
      header: i18n.t(`${TRANSLATION}.${key}.header`),
      subHeader: i18n.t(`${TRANSLATION}.${key}.subHeader`),
      actionText: i18n.t(`${TRANSLATION}.${key}.actionText`),
      scoreText: i18n.t(`${TRANSLATION}.${key}.scoreText`),
    }
  }

  const getResumePreview = () => {
    if (!resumeId || !resume) return
    return formatDocumentPreviewUrl(resume as Resume, {
      size: isPhone ? PHONE_PREVIEW_WIDTH : DESKTOP_PREVIEW_WIDTH,
    })
  }

  return {
    resume,
    text: getText(),
    status: getStatus(),
    suggestion: suggestions?.[0],
    isLoading: isFetching && isLoading,
    resumePreview: getResumePreview(),
  }
}
